/**
 * Urban Customz — local landing pages (/pages/custom-t-shirts/<city>, etc.)
 *
 * Small on purpose. The discovery bundle (uc-discovery.js) is not loaded here,
 * so this file owns the two things a local page needs:
 *
 *   1. Context. The city being viewed is written into the SAME sessionStorage
 *      record (`uc_ctx`) that uc-discovery.js reads on /pages/request-a-quote,
 *      so a visitor who lands on the Brampton page and then asks for a quote
 *      arrives with "City: Brampton" already attached. Values are pattern-
 *      checked before they are stored, exactly as uc-discovery.js does, so
 *      nothing arbitrary from a URL can reach the page or the quote email.
 *
 *   2. Reporting. Everything goes through window.ucTrack (uc-mode.js), which
 *      already fans out to the GTM dataLayer, gtag, the Meta pixel and
 *      Shopify web pixels. No new analytics library, no new pixel.
 *
 * This file fires exactly one event of its own, local_page_view. The CTA
 * events on these pages — design_start_clicked, request_quote_clicked,
 * call_clicked, store_clicked, local_service_clicked — are declared on the
 * links as data-uc-track and reported by uc-mode.js, so they land in the same
 * place as every other CTA on the site and are not counted twice.
 */
(function () {
  var doc = document;
  var root = doc.querySelector('[data-uc-local]');
  if (!root) return;

  var CTX_STORE = 'uc_ctx';
  /* Mirrors uc-discovery.js. `city` is the key this page adds. */
  var CTX_PATTERNS = {
    city: /^[a-z0-9-]{1,40}$/,
    province: /^[a-z0-9-]{1,40}$/,
    industry: /^[a-z0-9-]{1,60}$/,
    goal: /^[a-z0-9-]{1,60}$/,
    capability: /^[a-z0-9-]{1,60}$/,
    product: /^[a-z0-9-]{1,80}$/,
    qty: /^[0-9]{1,7}$/,
    timing: /^[a-z0-9-]{1,30}$/,
    region: /^(ca|us|other)$/,
    source: /^[a-z0-9:_.-]{1,60}$/,
    /* Storefront language the visitor landed in ("en", "fr"), from <html lang>. */
    locale: /^[a-z]{2}(-[a-z]{2})?$/
  };
  var CTX_KEYS = Object.keys(CTX_PATTERNS);

  function clean(key, value) {
    if (value == null) return null;
    var v = String(value).trim().toLowerCase();
    var re = CTX_PATTERNS[key];
    return re && re.test(v) ? v : null;
  }

  function read() {
    var out = {};
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(CTX_STORE) || '{}');
      CTX_KEYS.forEach(function (k) {
        var v = clean(k, saved[k]);
        if (v) out[k] = v;
      });
    } catch (e) {
      /* blocked storage — context is a convenience, never a requirement */
    }
    return out;
  }

  var state = read();

  function set(patch) {
    Object.keys(patch || {}).forEach(function (k) {
      var v = clean(k, patch[k]);
      if (v) {
        state[k] = v;
      } else {
        delete state[k];
      }
    });
    try {
      window.sessionStorage.setItem(CTX_STORE, JSON.stringify(state));
    } catch (e) {
      /* noop */
    }
  }

  /* Append the live context to a URL without clobbering what it already has.
     Done on click rather than in the markup, so crawlers only ever see clean
     internal links. */
  function decorate(href) {
    if (!href) return href;
    /* In-page anchors and non-http schemes are left alone. Resolving
       "#services" against the origin would turn the hero's own jump link into
       a link to the homepage. */
    if (href.charAt(0) === '#') return href;
    if (/^(tel|mailto|sms):/i.test(href)) return href;
    var url;
    try {
      url = new URL(href, window.location.origin);
    } catch (e) {
      return href;
    }
    if (url.origin !== window.location.origin) return href;
    CTX_KEYS.forEach(function (k) {
      if (state[k] && !url.searchParams.has(k)) url.searchParams.set(k, state[k]);
    });
    return url.pathname + (url.search || '') + (url.hash || '');
  }

  function track(name, params) {
    var payload = {};
    Object.keys(state).forEach(function (k) {
      payload['ctx_' + k] = state[k];
    });
    Object.keys(params || {}).forEach(function (k) {
      payload[k] = params[k];
    });
    if (typeof window.ucTrack === 'function') {
      window.ucTrack(name, payload);
      return;
    }
    /* uc-mode.js has not run yet (or failed). Fall back to the same pipeline. */
    try {
      if (Array.isArray(window.dataLayer)) {
        var ev = { event: name };
        Object.keys(payload).forEach(function (k) {
          ev[k] = payload[k];
        });
        window.dataLayer.push(ev);
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
      }
    } catch (e) {
      /* analytics must never break the page */
    }
  }

  /* --- Boot --------------------------------------------------------------- */
  var city = root.getAttribute('data-uc-city') || '';
  var pageType = root.getAttribute('data-uc-local') || '';
  var source = root.getAttribute('data-uc-source') || '';
  var province = root.getAttribute('data-uc-province') || '';
  var locale = String(doc.documentElement.getAttribute('lang') || '').toLowerCase().slice(0, 5);

  set({ city: city, source: source, province: province, locale: locale });
  track('local_page_view', { city: city, local_page: pageType, locale: locale });

  /* --- Clicks --------------------------------------------------------------
     uc-mode.js already reports every [data-uc-track] click through the same
     pipeline, and each event carries page_path — which is what attributes a
     design start, quote request or call back to this city's page. Reporting
     them again here would double every conversion, so this handler only does
     the one thing uc-mode.js cannot: carry the city into the outgoing URL.

     Capture phase, so the href is rewritten before the browser navigates. */
  doc.addEventListener(
    'click',
    function (e) {
      var link = e.target.closest ? e.target.closest('a[data-uc-ctx-link]') : null;
      if (!link || link.getAttribute('data-uc-ctx-done') === '1') return;

      var linkSource = link.getAttribute('data-uc-source');
      if (linkSource) set({ source: linkSource });

      link.setAttribute('href', decorate(link.getAttribute('href')));
      link.setAttribute('data-uc-ctx-done', '1');
    },
    true
  );
})();
